import type { CurrentUser } from "@/features/auth/currentUserTypes";
import { formatDashboardCount } from "@/features/dashboard/metricFormatters";
import type { DashboardPeriodState } from "@/features/dashboard/period";
import type {
  AdminHelperDashboardAnalytics,
  AdminHelperDashboardOperations,
  DashboardCountMetric,
  DashboardOperationalCountMetric,
} from "@/features/dashboard/types";
import { DashboardFrame } from "@/components/dashboard/ledger/DashboardFrame";
import { DashboardLoadSituation } from "@/components/dashboard/ledger/DashboardLoadSituation";
import { DashboardMetricStrip } from "@/components/dashboard/ledger/DashboardMetricStrip";
import { DashboardQualityOutcomes } from "@/components/dashboard/ledger/DashboardQualityOutcomes";
import { DashboardQuantityBreakdown } from "@/components/dashboard/ledger/DashboardQuantityBreakdown";
import { DashboardSection } from "@/components/dashboard/ledger/DashboardSection";

const ADMIN_HELPER_DASHBOARD_CSS = `
  .admin-helper-dashboard { min-width: 0; color: var(--ledger-text); }
  .admin-helper-dashboard__stack { display: grid; gap: 18px; min-width: 0; }
  .admin-helper-dashboard__operations {
    display: grid;
    gap: 14px;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    align-items: stretch;
    min-width: 0;
  }
  .admin-helper-dashboard__loads,
  .admin-helper-dashboard__quotation-region,
  .admin-helper-dashboard__quality,
  .admin-helper-dashboard__quantities { min-width: 0; }
  .admin-helper-dashboard__loads,
  .admin-helper-dashboard__quotation-region {
    display: flex;
  }
  .admin-helper-dashboard__loads > .dashboard-ledger-load,
  .admin-helper-dashboard__quotation-region > .dashboard-ledger-section {
    flex: 1;
  }
  .admin-helper-dashboard__quotation-section {
    display: grid;
    grid-template-rows: auto minmax(0, 1fr);
    margin-top: 0;
  }
  .admin-helper-dashboard__quotation-section .dashboard-ledger-section__body {
    display: flex;
    min-height: 0;
  }
  .admin-helper-dashboard__quotation {
    display: grid;
    flex: 1;
    align-content: center;
    gap: 0.65rem;
    width: 100%;
    min-height: 7rem;
    padding: 1rem;
    border: 1px solid var(--ledger-border);
    background: var(--ledger-surface);
    color: var(--ledger-text);
    font: inherit;
    text-align: left;
  }
  button.admin-helper-dashboard__quotation { cursor: pointer; }
  button.admin-helper-dashboard__quotation:hover {
    background: var(--ledger-surface-muted);
  }
  button.admin-helper-dashboard__quotation:focus-visible {
    position: relative;
    z-index: 1;
    outline: 2px solid var(--ledger-accent);
    outline-offset: -2px;
  }
  .admin-helper-dashboard__quotation-status {
    display: inline-flex;
    width: fit-content;
    padding: 0.25rem 0.5rem;
    border: 1px solid var(--ledger-border);
    border-radius: 999px;
    color: var(--ledger-muted);
    font-size: 0.75rem;
    font-weight: 800;
  }
  .admin-helper-dashboard__quotation-date {
    color: var(--ledger-text);
    font-family: "Barlow Condensed", "Arial Narrow", sans-serif;
    font-size: 1.35rem;
    font-weight: 700;
    line-height: 1;
  }
  .admin-helper-dashboard__quotation-count,
  .admin-helper-dashboard__quotation-empty {
    color: var(--ledger-muted);
    font-size: 0.875rem;
    margin: 0;
  }
  .admin-helper-dashboard__quality .dashboard-ledger-quality {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .admin-helper-dashboard__quality .dashboard-ledger-quality__item:nth-child(2n) {
    border-right: 0;
  }
  .admin-helper-dashboard__quality .dashboard-ledger-quality__item:nth-child(n + 3) {
    border-top: 1px solid var(--ledger-border);
  }
  @media (max-width: 900px) {
    .admin-helper-dashboard__operations { grid-template-columns: minmax(0, 1fr); }
  }
  @media (max-width: 400px) {
    .admin-helper-dashboard__quality .dashboard-ledger-quality {
      grid-template-columns: minmax(0, 1fr);
    }
    .admin-helper-dashboard__quality .dashboard-ledger-quality__item {
      border-right: 0;
    }
    .admin-helper-dashboard__quality .dashboard-ledger-quality__item:nth-child(n + 2) {
      border-top: 1px solid var(--ledger-border);
    }
  }
`;

export type AdminHelperDashboardNavigation =
  | { to: "/pedidos" | "/cargas" }
  | {
      to: "/cotacoes/$quotationId";
      params: { quotationId: string };
    };

export interface AdminHelperDashboardProps {
  user: CurrentUser;
  analytics: AdminHelperDashboardAnalytics;
  operations?: AdminHelperDashboardOperations;
  period: DashboardPeriodState;
  pendingPeriod?: DashboardPeriodState | null;
  updating?: boolean;
  canManageQuotation: boolean;
  onPeriodChange: (period: DashboardPeriodState) => void;
  onNavigate: (destination: AdminHelperDashboardNavigation) => void;
  onRetryAnalytics: () => void;
}

const asCountMetric = (
  metric: DashboardOperationalCountMetric,
): DashboardCountMetric =>
  metric.status === "available"
    ? {
        status: "available",
        unit: "count",
        value: metric.count,
        comparison: metric.comparison,
      }
    : {
        status: metric.status,
        unit: "count",
        reason_code: metric.reason_code,
      };

const quotationStatusLabel = (
  status: "not_started" | "in_progress" | "completed",
) =>
  status === "not_started"
    ? "Não iniciada"
    : status === "in_progress"
      ? "Em andamento"
      : "Concluída";

const formatQuotationDate = (date: string) => {
  const [year, month, day] = date.split("-");
  return year && month && day ? `${day}/${month}/${year}` : date;
};

function ActiveQuotation({
  operations,
  canManageQuotation,
  onNavigate,
}: {
  operations?: AdminHelperDashboardOperations;
  canManageQuotation: boolean;
  onNavigate: (destination: AdminHelperDashboardNavigation) => void;
}) {
  const quotation = operations?.active_quotation;
  const itemCount = quotation
    ? formatDashboardCount(quotation.item_count)
    : null;
  const itemLabel = quotation?.item_count === 1 ? "item" : "itens";
  const quotationSummary = quotation ? (
    <>
      <span className="admin-helper-dashboard__quotation-status">
        {quotationStatusLabel(quotation.status)}
      </span>
      <strong className="admin-helper-dashboard__quotation-date">
        {formatQuotationDate(quotation.date)}
      </strong>
      <span className="admin-helper-dashboard__quotation-count">
        {itemCount?.full} {itemLabel}
      </span>
    </>
  ) : null;
  return (
    <DashboardSection
      title="Cotação ativa"
      className="admin-helper-dashboard__quotation-section"
    >
      {quotation && canManageQuotation ? (
        <button
          type="button"
          className="admin-helper-dashboard__quotation"
          data-testid="admin-helper-active-quotation"
          aria-label={`Abrir cotação ativa de ${formatQuotationDate(quotation.date)}: ${quotationStatusLabel(quotation.status)}, ${itemCount?.full} ${itemLabel}`}
          onClick={() =>
            onNavigate({
              to: "/cotacoes/$quotationId",
              params: { quotationId: quotation.id },
            })
          }
        >
          {quotationSummary}
        </button>
      ) : quotation ? (
        <div
          className="admin-helper-dashboard__quotation"
          data-testid="admin-helper-active-quotation"
        >
          {quotationSummary}
        </div>
      ) : (
        <div
          className="admin-helper-dashboard__quotation"
          data-testid="admin-helper-active-quotation"
        >
          {quotation === undefined ? (
            <p
              className="admin-helper-dashboard__quotation-empty"
              role="status"
            >
              Atualizando cotação ativa.
            </p>
          ) : (
            <p
              className="admin-helper-dashboard__quotation-empty"
              role="status"
            >
              Nenhuma cotação ativa.
            </p>
          )}
        </div>
      )}
    </DashboardSection>
  );
}

export function AdminHelperDashboard({
  user,
  analytics,
  operations,
  period,
  pendingPeriod,
  updating = false,
  canManageQuotation,
  onPeriodChange,
  onNavigate,
}: AdminHelperDashboardProps) {
  const openCargas = () => onNavigate({ to: "/cargas" });
  const checkedQuantities =
    analytics.metrics.checked_quantities.status === "available"
      ? analytics.metrics.checked_quantities.quantities
      : [];
  const alerts: DashboardCountMetric = operations
    ? {
        status: "available",
        unit: "count",
        value: operations.alerts.unresolved,
      }
    : {
        status: "unavailable",
        unit: "count",
        reason_code: "operations_loading",
      };

  return (
    <DashboardFrame
      title="Painel operacional"
      className="admin-helper-dashboard"
      period={period}
      pendingPeriod={pendingPeriod}
      periodMetadata={analytics.period}
      onPeriodChange={onPeriodChange}
      updating={updating}
    >
      <style>{ADMIN_HELPER_DASHBOARD_CSS}</style>
      <div
        className="admin-helper-dashboard__stack"
        data-dashboard-role={user.role}
      >
        <div data-testid="admin-helper-dashboard-headlines">
          <DashboardMetricStrip
            ariaLabel="Indicadores operacionais"
            items={[
              {
                id: "checked-loads",
                label: "Cargas conferidas",
                metric: asCountMetric(analytics.metrics.checked_loads),
                onSelect: openCargas,
              },
              {
                id: "open-loads",
                label: "Cargas abertas",
                metric: operations
                  ? {
                      status: "available",
                      unit: "count",
                      value: operations.open_loads.total,
                    }
                  : asCountMetric(analytics.metrics.open_loads),
                onSelect: openCargas,
              },
              {
                id: "alerts",
                label: "Alertas pendentes",
                metric: alerts,
              },
              {
                id: "divergences",
                label: "Divergências",
                metric: asCountMetric(analytics.metrics.divergences),
                comparisonGoal: "lower-is-better",
                onSelect: openCargas,
              },
            ]}
          />
        </div>

        <div
          className="admin-helper-dashboard__operations"
          data-testid="admin-helper-dashboard-operations"
          data-layout="paired-equal-height"
        >
          <div
            className="admin-helper-dashboard__quotation-region"
            data-testid="admin-helper-dashboard-quotation"
          >
            <ActiveQuotation
              operations={operations}
              canManageQuotation={canManageQuotation}
              onNavigate={onNavigate}
            />
          </div>
          <div
            className="admin-helper-dashboard__loads"
            data-testid="admin-helper-dashboard-loads"
          >
            <DashboardLoadSituation
              mode="operational"
              situation={analytics.sections.load_situation}
              onSelectState={openCargas}
            />
          </div>
        </div>

        <div
          className="admin-helper-dashboard__quality"
          data-testid="admin-helper-dashboard-quality"
        >
          <DashboardQualityOutcomes
            outcomes={analytics.sections.divergence_outcomes}
            ariaLabel="Resultado das divergências"
          />
        </div>

        <div
          className="admin-helper-dashboard__quantities"
          data-testid="admin-helper-dashboard-quantities"
        >
          <DashboardQuantityBreakdown
            quantities={checkedQuantities}
            ariaLabel="Quantidades conferidas"
          />
        </div>
      </div>
    </DashboardFrame>
  );
}
